/**
 * Static SVG illustrations for the landing page (authored here, no user input).
 * Kept as markup strings because they rely on SMIL <animate> and gradient defs.
 */

const FONT = "font-family:Plus Jakarta Sans,sans-serif";

const bar = (x: number, topY: number, h: number, from: number, delay: string, label: string) => `
  <rect x="${x}" y="230" width="72" height="190" rx="12" fill="#eef3fa"/>
  <rect x="${x}" y="${topY}" width="72" height="${h}" rx="12" fill="url(#rl-bg1)">
    <animate attributeName="height" values="${from};${h};${h};${from}" keyTimes="0;.35;.85;1" dur="8s" begin="${delay}" repeatCount="indefinite"/>
    <animate attributeName="y" values="${420 - from};${topY};${topY};${420 - from}" keyTimes="0;.35;.85;1" dur="8s" begin="${delay}" repeatCount="indefinite"/>
  </rect>
  <text x="${x + 36}" y="442" text-anchor="middle" style="${FONT};font-size:11px;font-weight:700;fill:#3b4456">${label}</text>`;

const step = (cx: number, n: string, label: string, start: number) => {
  const end = start + 0.25;
  // keyTimes must stay within 0..1, so the last step's fade-out point is clamped.
  const times = `0;${start.toFixed(2)};${(start + 0.001).toFixed(3)};${end.toFixed(2)};${Math.min(end + 0.001, 1).toFixed(3)};1`;
  return `
  <circle cx="${cx}" cy="476" r="9" fill="#ffffff" stroke="#cfdcf2" stroke-width="1.5"/>
  <circle cx="${cx}" cy="476" r="9" fill="#2f6bd8" opacity="0"><animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="${times}" dur="8s" repeatCount="indefinite"/></circle>
  <text x="${cx}" y="480" text-anchor="middle" style="${FONT};font-size:9px;font-weight:800;fill:#6b7487">${n}</text>
  <text x="${cx}" y="500" text-anchor="middle" style="${FONT};font-size:10px;font-weight:600;fill:#6b7487">${label}</text>`;
};

const dust = [
  [560, 427, "0"],
  [183, 532, "-1.4"],
  [83, 365, "-2.1"],
  [149, 158, "-2.8"],
  [524, 172, "-4.2"],
]
  .map(([cx, cy, d]) => `<circle class="rl-dust" cx="${cx}" cy="${cy}" r="2" fill="#93b4e0" style="animation-delay:${d}s"/>`)
  .join("");

export const HERO_SVG = `
<svg viewBox="0 0 660 660" role="img" aria-label="Animated ReImagine 2.0 dashboard: maturity scores for strategy, organization and technical readiness moving through initiate, inputs, insights and roadmap">
  <defs>
    <radialGradient id="rl-isph" cx=".42" cy=".36" r=".7"><stop offset="0" stop-color="#ffffff"/><stop offset=".6" stop-color="#eef3fa"/><stop offset="1" stop-color="#d9e3f1"/></radialGradient>
    <linearGradient id="rl-bg1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#8cc8ff"/><stop offset=".45" stop-color="#4aa3ef"/><stop offset="1" stop-color="#2f6bd8"/></linearGradient>
    <clipPath id="rl-iclip"><circle cx="330" cy="330" r="274"/></clipPath>
  </defs>
  <circle cx="330" cy="330" r="318" fill="none" stroke="#cbd5e1" stroke-dasharray="2 7"/>
  <circle class="rl-spin" cx="330" cy="330" r="300" fill="none" stroke="#cbd5e1" stroke-width="1.2" stroke-dasharray="1 10"/>
  <circle cx="330" cy="330" r="276" fill="url(#rl-isph)" stroke="#ffffff" stroke-width="2"/>
  <ellipse cx="330" cy="520" rx="200" ry="14" fill="#1a2233" opacity=".05"/>
  ${dust}
  <g clip-path="url(#rl-iclip)">
    <rect x="166" y="176" width="328" height="348" rx="22" fill="#ffffff" stroke="#e3e8f0"/>
    <circle cx="192" cy="204" r="4" fill="#4aa3ef"/>
    <text x="204" y="208" style="${FONT};font-size:10px;font-weight:800;letter-spacing:.14em;fill:#6b7487">MATURITY SCORE</text>
    <rect x="404" y="194" width="70" height="20" rx="10" fill="#eef4fd"/>
    <text x="439" y="208" text-anchor="middle" style="${FONT};font-size:10px;font-weight:800;fill:#2f6bd8">LIVE</text>
    ${bar(196, 270, 150, 70, "-0.0s", "Strategy")}
    ${bar(292, 315, 105, 50, "-0.4s", "Organization")}
    ${bar(388, 250, 170, 85, "-0.8s", "Technical")}
    ${step(206, "01", "Initiate", 0)}
    ${step(288, "02", "Inputs", 0.25)}
    ${step(370, "03", "Insights", 0.5)}
    ${step(452, "04", "Roadmap", 0.75)}
  </g>
</svg>`;

/** Domain cards to Outcomes: a faint track, flowing dashes and a comet per lane (animated by .rl-flowwrap.is-active). */
const LANES = [
  { d: "M0 50 C60 50 60 150 120 150", delay: "0.1s" },
  { d: "M0 150 H120", delay: "0.35s" },
  { d: "M0 250 C60 250 60 150 120 150", delay: "0.6s" },
];

const laneMarkup = LANES.map(
  (l) => `
  <path d="${l.d}" class="rl-con__track"/>
  <path d="${l.d}" class="rl-con__flow" stroke="url(#rl-flowg)"/>
  <circle r="4" class="rl-con__comet"><animateMotion dur="2.4s" begin="${l.delay}" repeatCount="indefinite" path="${l.d}"/></circle>`,
).join("");

export const CONNECTOR_SVG = `
<svg class="rl-con" viewBox="0 0 120 300" preserveAspectRatio="none" aria-hidden="true">
  <defs>
    <linearGradient id="rl-flowg" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="120" y2="0">
      <stop offset="0" stop-color="#17b0fd"/><stop offset="1" stop-color="#102478"/>
    </linearGradient>
  </defs>${laneMarkup}
</svg>`;
